// Raw markdown-style content for the four legal pages. Rendered via lib/markdown.jsx.
// Source: provided by MetaxuOne

export const privacyContent = `# Privacy Policy

**Metaxu One Private Limited**

**Effective Date:** 06/07/2026
**Last Updated:** 06/07/2026

Metaxu One Private Limited ("**Metaxu One**", "**Company**", "**we**", "**us**", or "**our**"), a company incorporated under the laws of India and having its registered office in Mumbai, Maharashtra, India, is committed to protecting the privacy and personal data of individuals who interact with our website, products, services, applications, platforms, and business operations.

This Privacy Policy explains how we collect, use, process, store, disclose, transfer, and protect personal data in compliance with applicable laws, including:

* India's **Digital Personal Data Protection Act, 2023 (DPDP Act)**
* The **General Data Protection Regulation (GDPR)** of the European Union, where applicable
* The **UK GDPR** and Data Protection Act 2018, where applicable
* Other applicable international privacy and data protection requirements relevant to our operations

Although Metaxu One Private Limited is established and operated from Mumbai, India, this Privacy Policy is designed to provide a high standard of privacy protection to all users globally.

## 1. Identity of the Data Fiduciary / Data Controller

For purposes of applicable privacy laws:

**Metaxu One Private Limited**
Mumbai, Maharashtra, India

Email: privacy@metaxuone.com

Where required under applicable law, Metaxu One Private Limited acts as the "Data Fiduciary" under the DPDP Act and the "Data Controller" under GDPR and similar laws.

## 2. Scope of this Privacy Policy

This Privacy Policy applies to:

* Visitors to our website
* Customers and prospective customers
* Business partners and vendors
* Users of our applications and services
* Individuals communicating with us
* Individuals whose information is provided to us in connection with our business operations

This Privacy Policy does not apply to third-party websites, products, or services that may be linked from our website.

## 3. Personal Data We Collect

We may collect the following categories of personal data:

### A. Identity Information

* Full name
* Username
* Profile information
* Government-issued identifiers (only when legally required)

### B. Contact Information

* Email address
* Phone number
* Postal address
* Business contact details

### C. Account Information

* Login credentials
* User preferences
* Authentication information

### D. Transaction Information

* Purchase records
* Billing information
* Subscription details
* Payment status

**Note:** Payment card information is generally processed by authorized payment service providers and is not stored by Metaxu One unless specifically required.

### E. Technical Information

* IP address
* Browser type and version
* Device identifiers
* Operating system
* Network information
* Log files

### F. Usage Information

* Website interactions
* Service usage patterns
* Session data
* Clickstream information
* Feature engagement data

### G. Marketing Information

* Communication preferences
* Newsletter subscriptions
* Consent records
* Marketing engagement data

### H. Customer Support Information

* Support requests
* Correspondence records
* Feedback
* Complaint details

## 4. Sensitive Personal Data

Metaxu One does not intentionally collect sensitive personal data unless:

* Required by law
* Necessary for providing requested services
* Explicitly provided with valid consent
* Permitted under applicable legal requirements

Where sensitive personal data is processed, we implement enhanced safeguards and process such data only for legitimate and lawful purposes.

## 5. How We Collect Personal Data

We collect personal data through:

### Direct Collection

When you register an account, contact us, purchase products or services, submit forms, subscribe to communications, or participate in surveys or events.

### Automated Collection

Through cookies, analytics technologies, server logs, website monitoring tools, and similar technologies.

### Third-Party Sources

We may receive information from business partners, service providers, payment processors, publicly available sources, and social media platforms where legally permitted.

## 6. Purposes of Processing

We process personal data for service delivery, business operations, security, communications, legal compliance, and analytics and improvement.

## 7. Legal Basis for Processing

Where GDPR or similar laws apply, we process personal data based on one or more of the following legal grounds: consent, contractual necessity, legal obligation, and legitimate interests.

Such processing is balanced against individual rights and freedoms.

## 8. Consent Management

Where consent is required:

* Consent will be obtained through clear and affirmative action
* Consent may be withdrawn at any time
* Withdrawal of consent does not affect prior lawful processing

To withdraw consent, contact: **privacy@metaxuone.com**

## 9. Children's Privacy

Our services are not directed toward children unless specifically stated. Where processing involves children and applicable law requires parental or guardian consent, we will obtain verifiable consent before processing such data.

## 10. Cookies and Tracking Technologies

We use cookies and similar technologies for website functionality, security, analytics, performance optimization, and user preferences.

Users may manage cookie preferences through browser settings or consent management tools provided on the website.

## 11. Disclosure of Personal Data

We may disclose personal data to service providers, professional advisors, regulatory authorities, and in connection with corporate transactions. Recipients are contractually required to maintain appropriate confidentiality and security standards.

## 12. International Data Transfers

Metaxu One Private Limited is based in Mumbai, India. Personal data may be processed or stored in India or other jurisdictions where our service providers operate. Where international transfers occur, we implement appropriate safeguards including contractual protections, data processing agreements, security controls, and lawful transfer mechanisms recognized under applicable regulations.

## 13. Data Security

We implement reasonable technical, organizational, and administrative safeguards, including encryption, access controls, authentication mechanisms, network security measures, monitoring and logging, secure development practices, and vendor security assessments.

While we strive to protect personal data, no method of transmission or storage can be guaranteed to be completely secure.

## 14. Data Retention

We retain personal data only for as long as necessary to provide services, fulfill contractual obligations, meet legal requirements, resolve disputes, enforce agreements, and protect legitimate business interests. Upon expiration of retention requirements, data will be securely deleted, anonymized, or irreversibly de-identified where feasible.

## 15. Rights of Individuals

Subject to applicable law, individuals may have the right to access, correction, erasure, restriction, objection, data portability, withdraw consent, and grievance redressal. We may require identity verification before responding to requests.

## 16. Rights under the Digital Personal Data Protection Act, 2023

Where applicable under Indian law, Data Principals may exercise rights including the right to access information about personal data processing, right to correction and erasure, right to grievance redressal, and the right to nominate another person to exercise rights in accordance with applicable law.

## 17. Grievance Redressal Officer

In accordance with applicable Indian laws, including the DPDP Act, users may contact our Grievance Officer:

**Grievance Officer**
Metaxu One Private Limited
Mumbai, Maharashtra, India

Email: grievance@metaxuone.com

We will make reasonable efforts to acknowledge and address grievances within applicable legal timelines.

## 18. Automated Decision-Making and Profiling

Metaxu One does not make decisions producing legal or similarly significant effects solely through automated processing unless authorized by law, necessary for service delivery, or supported by appropriate safeguards. Where applicable, individuals may request human review.

## 19. Third-Party Services

Our website and services may integrate with third-party providers. Such providers may have independent privacy policies and data processing practices. Metaxu One is not responsible for the privacy practices of independent third parties.

## 20. Data Breach Response

In the event of a personal data breach, we will assess the nature and impact of the incident, implement containment and remediation measures, notify affected individuals where required by law, and notify relevant regulatory authorities where legally required.

## 21. Do Not Track Signals

Some web browsers may transmit "Do Not Track" signals. Because no uniform industry standard currently exists for such signals, our systems may not respond to all Do Not Track requests. Users may manage privacy preferences through browser settings and cookie controls.

## 22. Changes to this Privacy Policy

We may update this Privacy Policy from time to time. Material changes will be communicated through website notices, account notifications, and email communications where appropriate.

## 23. Contact Us

For privacy-related questions, requests, or complaints, please contact:

**Metaxu One Private Limited**
Mumbai, Maharashtra, India

Privacy Contact: privacy@metaxuone.com
Grievance Officer: grievance@metaxuone.com

## Jurisdiction and Governing Law

This Privacy Policy shall be governed by and construed in accordance with the laws of India. Any disputes arising from or relating to this Privacy Policy shall be subject to the exclusive jurisdiction of the competent courts located in **Mumbai, Maharashtra, India**, except where mandatory consumer or data protection laws of another jurisdiction provide otherwise.

**© Metaxu One Private Limited. All Rights Reserved.**
`;

export const termsContent = `# Terms and Conditions

**Metaxu One Private Limited**

**Effective Date:** 06/07/2026
**Last Updated:** 06/07/2026

Welcome to the website, applications, products, platforms, and services operated by **Metaxu One Private Limited** ("**Metaxu One**", "**Company**", "**we**", "**us**", or "**our**").

These Terms and Conditions ("**Terms**") govern your access to and use of our website, products, software, applications, digital platforms, content, and services (collectively, the "**Services**").

By accessing, browsing, registering for, or using any of our Services, you acknowledge that you have read, understood, and agree to be legally bound by these Terms. If you do not agree to these Terms, you must not access or use our Services.

## 1. Company Information

**Metaxu One Private Limited**
Mumbai, Maharashtra, India

Email: legal@metaxuone.com

Metaxu One Private Limited is incorporated under the laws of India and operates from Mumbai, Maharashtra.

## 2. Acceptance of Terms

By using our Services, you represent and warrant that:

* You are legally capable of entering into a binding agreement
* You are at least 18 years of age or the age of majority in your jurisdiction
* You have the authority to bind any organization you represent
* Your use of the Services complies with all applicable laws and regulations

If you are accessing the Services on behalf of a company, organization, or entity, you agree to these Terms on behalf of that entity.

## 3. Scope of Services

Metaxu One may provide, develop, operate, maintain, or offer websites, software applications, SaaS platforms, artificial intelligence services, technology solutions, APIs and integrations, digital products, consulting services, enterprise solutions, cloud-based services, and future products and services introduced by the Company.

We reserve the right to modify, suspend, discontinue, or replace any Service at our discretion.

## 4. User Accounts

Where account registration is required, you agree to provide accurate and complete information, maintain current account information, protect your login credentials, immediately notify us of unauthorized access, and accept responsibility for activities occurring under your account.

We reserve the right to suspend or terminate accounts that violate these Terms.

## 5. Permitted Use

You may use the Services solely for lawful purposes and in accordance with these Terms.

You agree not to:

* Violate applicable laws or regulations
* Infringe intellectual property rights
* Upload malicious software or harmful code
* Interfere with Service operations
* Circumvent security measures
* Attempt unauthorized access to systems
* Harvest or collect user information without authorization
* Engage in fraudulent, deceptive, or misleading conduct
* Use automated tools that impose unreasonable loads on infrastructure
* Reverse engineer software except where expressly permitted by law

## 6. User Content

Users may submit, upload, publish, transmit, or otherwise provide content including text, images, documents, audio, video, data, feedback, and communications ("**User Content**").

You retain ownership of User Content submitted by you.

By submitting User Content, you grant Metaxu One a worldwide, non-exclusive, royalty-free, transferable, sublicensable license to host, store, process, display, reproduce, modify (for technical purposes), and distribute solely to provide, operate, improve, and secure the Services.

## 7. Intellectual Property Rights

All rights, title, and interest in the Services remain the exclusive property of Metaxu One Private Limited or its licensors.

Except as expressly authorized, users may not copy, modify, reproduce, distribute, license, sell, or commercialize any portion of the Services.

## 8. Artificial Intelligence and Automated Services

Where Metaxu One provides AI-powered features or automated tools, users acknowledge that AI-generated outputs may contain inaccuracies, outputs should be independently verified before reliance, AI responses do not constitute professional advice, and AI-generated content may not always be unique.

Metaxu One does not guarantee the accuracy, completeness, legality, or suitability of AI-generated outputs. Users remain responsible for decisions made based on such outputs.

## 9. Feedback

Any suggestions, recommendations, ideas, or feedback submitted to Metaxu One may be used without restriction, compensation, or attribution. You grant the Company an irrevocable, perpetual, worldwide right to use such feedback for business purposes.

## 10. Subscription Services and Payments

Certain Services may require payment. Where applicable:

* Fees are displayed before purchase
* Taxes may be charged as required by law
* Payment obligations are non-cancellable except as required by law
* Subscription plans may renew automatically unless canceled

Users are responsible for maintaining valid payment information. Failure to pay may result in suspension or termination of Services.

## 11. Refund Policy

Unless otherwise stated in a separate written agreement, fees paid are generally non-refundable. Refund requests will be evaluated in accordance with applicable consumer protection laws. Statutory rights cannot be excluded.

## 12. Third-Party Services and Links

The Services may include third-party integrations, APIs, payment gateways, external websites, cloud providers, and software platforms. Metaxu One does not control and is not responsible for third-party services. Use of third-party services is governed by their respective terms and privacy policies.

## 13. Availability of Services

We strive to maintain Service availability but do not guarantee uninterrupted access. Services may experience scheduled maintenance, system upgrades, technical failures, network interruptions, security incidents, or force majeure events.

## 14. Security

Users are responsible for protecting account credentials, using secure devices and networks, and maintaining appropriate security practices. Metaxu One employs reasonable security measures but cannot guarantee absolute protection against cyber threats or unauthorized access.

## 15. Privacy

Collection and processing of personal information are governed by our Privacy Policy. By using the Services, you acknowledge that personal data may be processed in accordance with the Privacy Policy and applicable law.

## 16. Compliance with Laws

Users agree to comply with all applicable laws and regulations, including those relating to data protection, intellectual property, export controls, sanctions, anti-corruption, consumer protection, and cybersecurity.

## 17. Prohibited Activities

You may not use the Services for illegal activities, fraudulent conduct, money laundering, terrorist financing, harassment, hate speech, defamation, intellectual property infringement, distribution of malware, unauthorized surveillance, cyberattacks, or spam.

Metaxu One reserves the right to investigate and report unlawful conduct.

## 18. Suspension and Termination

We may suspend, restrict, or terminate access to the Services without prior notice where these Terms are violated, legal obligations require action, security risks are identified, fraudulent activity is suspected, or user conduct poses risk to the Company or others. Termination does not relieve outstanding obligations.

## 19. Disclaimer of Warranties

TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. METAXU ONE DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, ACCURACY, RELIABILITY, AND AVAILABILITY.

## 20. Limitation of Liability

TO THE MAXIMUM EXTENT PERMITTED BY LAW, METAXU ONE SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES, INCLUDING LOSS OF PROFITS, DATA, BUSINESS OPPORTUNITIES, OR BUSINESS INTERRUPTION ARISING FROM OR RELATED TO USE OF THE SERVICES.

WHERE LIABILITY CANNOT BE EXCLUDED, THE COMPANY'S AGGREGATE LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY THE USER TO THE COMPANY DURING THE TWELVE (12) MONTHS PRECEDING THE CLAIM.

## 21. Indemnification

You agree to defend, indemnify, and hold harmless Metaxu One Private Limited and its officers, directors, employees, affiliates, and agents from claims, losses, liabilities, damages, costs, and expenses arising from violation of these Terms, misuse of the Services, violation of law, or infringement of third-party rights.

## 22. Force Majeure

Metaxu One shall not be liable for delays or failures resulting from events beyond reasonable control, including natural disasters, epidemics, pandemics, government actions, civil unrest, labor disputes, internet outages, cyberattacks, and power failures.

## 23. Export Compliance and Sanctions

Users agree not to use the Services in violation of applicable export control, sanctions, or trade restriction laws. The Services may not be used where prohibited by law.

## 24. Governing Law

These Terms shall be governed by and construed in accordance with the laws of India, without regard to conflict of law principles.

## 25. Dispute Resolution

Any dispute, controversy, or claim arising out of or relating to these Terms shall first be attempted to be resolved through good-faith negotiations. If unresolved, disputes shall be subject to the exclusive jurisdiction of the competent courts located in **Mumbai, Maharashtra, India**.

## 26. Electronic Communications

By using the Services, you consent to receive communications electronically, including notices, disclosures, agreements, service updates, and legal communications.

## 27. Assignment

Metaxu One may assign, transfer, or delegate its rights and obligations under these Terms. Users may not assign their rights or obligations without prior written consent.

## 28. Severability

If any provision of these Terms is found unenforceable, the remaining provisions shall continue in full force and effect.

## 29. No Waiver

Failure to enforce any provision shall not constitute a waiver of that provision or any other rights.

## 30. Entire Agreement

These Terms, together with the Privacy Policy and any applicable service-specific agreements, constitute the entire agreement between the user and Metaxu One Private Limited regarding the Services.

## 31. Changes to These Terms

We may update these Terms from time to time. Updated versions will be published on the website with a revised effective date. Continued use of the Services after updates constitutes acceptance of the revised Terms.

## 32. Contact Information

For legal, compliance, or contractual inquiries:

**Metaxu One Private Limited**
Mumbai, Maharashtra, India

Email: legal@metaxuone.com

For general support: support@metaxuone.com

**Copyright © Metaxu One Private Limited. All Rights Reserved.**
`;

export const cookiesContent = `# Cookie Policy

**Metaxu One Private Limited**

**Effective Date:** 06/07/2026
**Last Updated:** 06/07/2026

This Cookie Policy explains how **Metaxu One Private Limited** ("**Metaxu One**," "**Company**," "**we**," "**us**," or "**our**") uses cookies, similar technologies, and related tracking mechanisms when you visit our website, applications, platforms, products, and services (collectively, the "**Services**").

This Cookie Policy should be read together with our Privacy Policy and Terms and Conditions.

By continuing to use our Services, you acknowledge that cookies and similar technologies may be used as described in this Policy, subject to your consent preferences and applicable law.

## 1. Company Information

**Metaxu One Private Limited**
Mumbai, Maharashtra, India

Email: privacy@metaxuone.com

## 2. What Are Cookies?

Cookies are small text files that are stored on your device when you visit a website. Cookies help websites function properly, improve security, remember preferences, analyze website performance, improve user experience, and support business operations.

Cookies generally do not access files stored on your device and cannot execute programs or deliver malware.

## 3. What Are Similar Technologies?

In addition to cookies, we may use web beacons, pixels, tags, local storage, session storage, SDKs, device identifiers, log files, and server-side tracking technologies.

For simplicity, all such technologies are collectively referred to as "Cookies" throughout this Policy unless otherwise specified.

## 4. Types of Cookies We Use

### A. Strictly Necessary Cookies

These cookies are essential for the operation, security, and integrity of the Services. Examples include authentication, session management, security controls, load balancing, fraud prevention, and network management. Without these cookies, certain features may not function properly.

### B. Functional Cookies

These cookies help remember language preferences, region settings, accessibility settings, user preferences, and saved configurations.

### C. Analytics Cookies

These cookies help us understand website traffic, user behavior, navigation patterns, feature usage, and service performance. Data is typically aggregated and used to improve Services.

### D. Performance Cookies

Performance cookies help optimize website speed, identify technical issues, improve system reliability, and monitor operational performance.

### E. Marketing and Advertising Cookies

Where permitted by law, these cookies may be used to measure advertising effectiveness, deliver relevant promotions, limit repetitive advertisements, and analyze campaign performance. Marketing cookies are deployed only where legally required consent has been obtained.

### F. Third-Party Cookies

Certain third-party providers may place cookies through our Services, including analytics providers, cloud providers, security providers, payment processors, customer support platforms, and marketing service providers. Metaxu One does not control third-party cookie technologies.

## 5. Categories of Information Collected Through Cookies

Depending on the technology used, cookies may collect IP address, browser type, device information, operating system, language preferences, geographic region, session information, website interactions, login status, referring URLs, usage statistics, and performance metrics.

We do not intentionally use cookies to collect sensitive personal information unless expressly disclosed and legally permitted.

## 6. Purposes of Cookie Usage

We use cookies for service delivery, security, performance, analytics, and compliance.

## 7. Legal Basis for Cookie Processing

Depending on applicable law and jurisdiction, cookie processing may rely on consent, legitimate interests, contractual necessity, and legal compliance.

## 8. Cookie Consent Management

Where required by applicable law, users will be provided with a consent management mechanism allowing them to accept all cookies, reject non-essential cookies, customize cookie preferences, and withdraw consent at any time.

Withdrawal of consent does not affect the lawfulness of processing conducted prior to withdrawal.

## 9. Managing Cookies

Users may control cookies through browser settings, device settings, consent preference tools, and third-party opt-out mechanisms. Most browsers permit blocking cookies, deleting cookies, and restricting tracking technologies. Disabling certain cookies may impact Service functionality.

## 10. Cookie Retention Periods

### Session Cookies

Automatically deleted when the browser closes.

### Persistent Cookies

Remain until expiration date is reached, user manually deletes them, or browser settings remove them.

## 11. International Data Transfers

Cookie-generated information may be processed in India and other jurisdictions where service providers operate. Where required by law, appropriate safeguards may be implemented.

## 12. Data Security

We implement reasonable safeguards designed to protect information collected through cookies, including encryption, access controls, monitoring systems, security testing, and vendor assessments.

No internet-based transmission or storage method can be guaranteed to be completely secure.

## 13. Third-Party Analytics and Advertising Services

We may use third-party technologies to analyze website usage, measure engagement, improve Services, and support business operations. Such providers may independently process information in accordance with their own privacy notices.

## 14. No Guarantee of Complete Tracking Prevention

Although users may block cookies or tracking technologies, Metaxu One does not guarantee that all tracking mechanisms can be completely disabled, browser settings will function identically across devices, third-party technologies will fully honor user preferences, or internet infrastructure beyond our control will not collect technical information.

## 15. Limitation of Liability Regarding Cookies

To the maximum extent permitted by applicable law, Metaxu One Private Limited shall not be liable for browser-level tracking technologies, third-party cookies, device manufacturer technologies, internet service provider activities, telecommunications network operations, third-party analytics processing, third-party advertising technologies, user configuration errors, or unauthorized third-party interception beyond reasonable security controls.

## 16. Third-Party Liability Disclaimer

Metaxu One does not own, operate, manage, or control third-party websites, advertising networks, analytics services, cloud providers, social media platforms, or technology vendors. Accordingly, we make no representations regarding their privacy practices, we are not responsible for their cookie deployments, and we are not liable for losses arising from third-party processing activities.

## 17. Compliance Statement

This Cookie Policy has been designed with consideration of applicable privacy and data protection principles, including the Digital Personal Data Protection Act, 2023 (India), the Information Technology Act, 2000 (India), GDPR (where applicable), UK GDPR (where applicable), ePrivacy principles (where applicable), and other applicable privacy regulations.

## 18. Governing Law

This Cookie Policy shall be governed by and construed in accordance with the laws of India.

## 19. Jurisdiction

Any dispute arising out of or relating to this Cookie Policy shall be subject to the exclusive jurisdiction of the competent courts located in **Mumbai, Maharashtra, India**, except where mandatory consumer protection or privacy laws provide otherwise.

## 20. Changes to This Cookie Policy

We may amend this Cookie Policy from time to time. Updated versions will be published on the website and become effective upon publication unless otherwise specified.

## 21. Contact Information

For questions regarding cookies, privacy, or compliance matters:

**Metaxu One Private Limited**
Mumbai, Maharashtra, India

Privacy Team: privacy@metaxuone.com
Legal Team: legal@metaxuone.com

## Acknowledgment

By accessing or using the Services, you acknowledge that you have read and understood this Cookie Policy and understand how cookies and related technologies may be used in connection with the Services, subject to applicable law and your consent preferences where required.

**Copyright © Metaxu One Private Limited. All Rights Reserved.**
**Version 1.0**
**Jurisdiction: Mumbai, Maharashtra, India**
`;

export const imprintContent = `# Imprint / Legal Notice

**Metaxu One Private Limited**

**Effective Date:** 06/07/2026
**Last Updated:** 06/07/2026

This Imprint ("Legal Notice") provides mandatory legal and corporate information regarding the ownership, operation, administration, and publication of the website, digital platforms, applications, products, services, and related online properties operated by **Metaxu One Private Limited**.

This Imprint is intended to satisfy applicable legal disclosure requirements and should be read together with our Privacy Policy, Cookie Policy, Terms and Conditions, and any service-specific agreements.

## 1. Website Owner and Operator

### Legal Entity

**Metaxu One Private Limited**

A company incorporated under the provisions of the Companies Act, 2013 and applicable laws of India.

### Registered Office

Metaxu One Private Limited
Mumbai, Maharashtra, India

### Corporate Identification Number (CIN)

[To be provided]

### Goods and Services Tax Identification Number (GSTIN)

[To be provided]

### Email

legal@metaxuone.com

### Website

https://www.metaxuone.com

## 2. Legal Representation

Unless otherwise specified, Metaxu One Private Limited acts through its duly appointed directors, officers, authorized representatives, and employees in accordance with applicable corporate laws and regulations.

For legal communications, notices, and compliance matters:

**Legal Department**
Metaxu One Private Limited
Mumbai, Maharashtra, India

Email: legal@metaxuone.com

## 3. Regulatory Status

Metaxu One Private Limited operates as a private limited company under Indian law.

Unless expressly stated on a specific service page, Metaxu One is not a licensed bank, financial institution, regulated securities intermediary, legal services provider, healthcare provider, or insurance company, and does not provide regulated professional services requiring governmental licensing unless expressly disclosed.

Any references to business, technology, software, automation, artificial intelligence, consulting, digital solutions, analytics, or platform services shall not be interpreted as regulated professional advice.

## 4. Website Content Responsibility

Metaxu One Private Limited is responsible for content created and published directly by the Company on this website, including corporate information, product information, service descriptions, policies, legal notices, blog articles, educational resources, and marketing materials.

The Company exercises reasonable care in preparing and publishing information but does not guarantee that all content will always be accurate, complete, current, error-free, or suitable for every purpose.

## 5. No Professional Advice

Unless expressly stated otherwise, information available through the website is provided solely for general informational purposes.

Nothing contained on this website constitutes legal advice, financial advice, investment advice, tax advice, accounting advice, medical advice, regulatory advice, or professional consulting advice.

Users should obtain independent professional advice before making decisions based on website content. Reliance on any information is solely at the user's own risk.

## 6. Intellectual Property Rights

All intellectual property rights associated with this website remain the exclusive property of Metaxu One Private Limited and/or its licensors.

Protected materials include website design, source code, software, databases, logos, trademarks, service marks, text, graphics, documents, videos, images, user interfaces, product names, and business processes.

No rights are granted except as expressly permitted under applicable law. Unauthorized use, copying, reproduction, modification, distribution, or commercialization is strictly prohibited.

## 7. Trademark Notice

"Metaxu One", the Metaxu One logo, product names, service names, slogans, branding elements, and related marks may constitute trademarks, trade names, service marks, or proprietary identifiers owned by Metaxu One Private Limited.

Unauthorized use is prohibited. Nothing in this Imprint grants any license or right to use such marks without prior written authorization.

## 8. Copyright Notice

Unless otherwise stated: **Copyright © Metaxu One Private Limited. All Rights Reserved.**

All website content is protected by applicable copyright laws and international intellectual property treaties.

Users may view website content and download content for personal and non-commercial use where permitted. Users may not reproduce content commercially, republish content, sell content, distribute content, or create derivative works without prior written permission.

## 9. Third-Party Content

This website may contain third-party materials, embedded content, APIs, integrations, links, and references. All rights in third-party content remain with respective owners. Metaxu One does not claim ownership of third-party intellectual property.

## 10. External Links Disclaimer

The website may contain links to external websites.

Metaxu One does not control third-party websites, does not monitor all external content, does not guarantee accuracy of external information, does not endorse all linked content, and does not assume responsibility for third-party websites.

Users access third-party websites at their own risk.

## 11. Technology and Service Availability Disclaimer

Metaxu One makes reasonable efforts to maintain website availability. However, we do not guarantee continuous availability, error-free operation, uninterrupted service, compatibility with all devices, compatibility with all browsers, or availability of all features in all jurisdictions.

## 12. Cybersecurity and Internet Risk Disclaimer

The internet is inherently subject to risks beyond the control of any organization.

While reasonable safeguards are implemented, Metaxu One cannot guarantee protection against cyberattacks, malware, unauthorized access, data interception, internet routing failures, third-party system breaches, and telecommunications failures.

Users remain responsible for maintaining appropriate cybersecurity measures on their own devices and networks.

## 13. Artificial Intelligence and Automated Systems Disclaimer

Where artificial intelligence, machine learning, automation, predictive technologies, or algorithmic systems are utilized, users acknowledge that outputs may contain inaccuracies, results may not always be complete, automated systems may generate unexpected outcomes, and human review may still be necessary.

Metaxu One does not warrant that AI-generated outputs are error-free, legally compliant, or suitable for any specific purpose. Users are responsible for validating outputs before relying on them.

## 14. Limitation of Liability

To the fullest extent permitted by applicable law, Metaxu One Private Limited, its directors, officers, employees, affiliates, contractors, licensors, and representatives shall not be liable for direct losses beyond legally mandated liability, indirect losses, consequential damages, special damages, incidental damages, punitive damages, loss of profits, loss of revenue, loss of business opportunities, loss of goodwill, loss of anticipated savings, data loss, or service interruption. This limitation applies regardless of the legal theory asserted.

## 15. User Responsibility

Users are solely responsible for compliance with applicable laws, evaluating information before reliance, maintaining account security, device security, data backups, and professional decision-making.

The Company shall not be responsible for decisions made by users based upon information obtained through the website.

## 16. Indemnification

Users agree to defend, indemnify, and hold harmless Metaxu One Private Limited and its officers, directors, employees, affiliates, licensors, agents, and contractors from any claims, losses, liabilities, damages, costs, penalties, and expenses arising from misuse of the website, violation of applicable law, violation of intellectual property rights, breach of contractual obligations, user-generated content, fraudulent activity, or unauthorized access attempts.

## 17. Compliance with Global Laws

Although Metaxu One operates from Mumbai, India, users may access the website globally. Users are responsible for ensuring that access and use of the website are lawful in their jurisdiction.

Nothing in this website constitutes an offer where such offer would be unlawful.

## 18. Data Protection Notice

Personal information may be processed in accordance with our Privacy Policy, Cookie Policy, and applicable data protection laws.

Users should review those policies for detailed information regarding data collection and processing practices.

## 19. Legal Notices and Communications

Official legal communications relating to this website must be directed to:

**Legal Department**
Metaxu One Private Limited
Mumbai, Maharashtra, India

Email: legal@metaxuone.com

Electronic communications shall be deemed received when successfully delivered to the designated communication channel.

## 20. Governing Law

This Imprint and all matters arising from or relating to the website shall be governed by and interpreted under the laws of India.

## 21. Exclusive Jurisdiction

Subject to any non-waivable statutory rights, all disputes, claims, proceedings, or controversies arising out of or relating to this Imprint, website access, website content, services, communications, or business relationships shall be subject to the exclusive jurisdiction of the competent courts located in **Mumbai, Maharashtra, India**.

Users expressly consent to such jurisdiction and venue.

## 22. Severability

If any provision of this Imprint is held invalid, unlawful, or unenforceable, the remaining provisions shall remain fully effective and enforceable.

## 23. No Waiver

Failure by Metaxu One to enforce any provision shall not constitute a waiver of any right or remedy.

## 24. Changes to This Imprint

Metaxu One reserves the right to update, revise, modify, or replace this Imprint at any time. Updated versions become effective upon publication unless otherwise stated.

## Contact Information

**Metaxu One Private Limited**
Mumbai, Maharashtra, India

**General Inquiries:** support@metaxuone.com
**Privacy Matters:** privacy@metaxuone.com
**Legal Matters:** legal@metaxuone.com
**Compliance Matters:** compliance@metaxuone.com

**Metaxu One Private Limited**
**All Rights Reserved.**
**Jurisdiction: Mumbai, Maharashtra, India**
**Version 1.0**
`;
